// Check against the plan --- the PO panel's tab for drafts (shared/po-plan-check.ts, worked out from synced data in an
// idle moment by <PlanCheckRunner>, usePlanCheck()): each line's drafted qty beside what the plan suggests now without this draft,
// the status, why (what changed since it was drafted), the order-by date and who set it; items due from this vendor
// that aren't on it. "Update selected" sets the ticked lines to the plan's quantity, takes off ticked lines the plan
// doesn't need now and adds ticked items, in one audited mutation (po.update_from_plan) shown at once; "Keep as they
// are" records the check without changes. Also the warning "Send to vendor" and "Mark as sent" show (never blocking);
// Packaging --- the same warning lists packaging parts whose revision lacks print files (packaging.ts); Product labels ---
// and products whose label has something off, once each.
import { AlertTriangle, CheckCircle2, Info, Package, Tag } from "lucide-react";
import { Fragment, useState } from "react";
import { coverDay } from "../../../../shared/cover.ts"; // Cover through ---
import { DEFAULTS, type Po, type PoLine, type VendorItem } from "../../../../shared/db.gen.ts";
import { fmt, planToday } from "../../../../shared/planning.ts";
import { lineDefaults, repriceFor } from "../../../../shared/po.ts";
import {
  CHECK_TOLERANCE_PCT,
  type CheckAddition,
  type CheckRow,
  type DraftCheck,
  isDifference,
  LINE_STATUS_LABELS,
  type LineStatus,
  needsWarning,
  overrideNote,
  planChanges,
  updateItems,
  whyOf,
} from "../../../../shared/po-plan-check.ts";
import { ago } from "../../lib/format.ts";
import { can, mutateWithResult } from "../../lib/mutate.ts";
import { me } from "../../lib/session.ts";
import { type Change, store, useRows } from "../../lib/sync.ts";
import { Badge, Button, cx } from "../../ui/controls.tsx";
import { type Column, DataTable, type TableSection } from "../../ui/DataTable.tsx";
import { DialogLayer } from "../../ui/DialogLayer.tsx";
import { Popover } from "../../ui/Popover.tsx";
import { toast } from "../../ui/toast.tsx";
import { fixTitle, gapText, LABEL_FIX_TITLE, LABELS_LATER, type LabelGap, type PrintGap, useOpenFix, useOpenLabel } from "./packaging.ts"; // Packaging ---

const TONE: Record<LineStatus, "neutral" | "accent" | "ok" | "warn" | "danger"> = {
  same: "ok",
  more: "warn",
  less: "accent",
  not_needed: "danger",
  unplanned: "neutral",
};
/** Status order for sorting: what needs doing first. */
const STATUS_RANK: Record<LineStatus | "due", number> = { not_needed: 0, more: 1, less: 2, due: 3, unplanned: 4, same: 5 };

/** A row of the check: an item on the draft, or (in its band) one due from the vendor that isn't on it. */
type PlanRow = { id: string; order: number; position: number | null; sku: string; name: string; reason: string; actionable: boolean } & (
  | { row: CheckRow; add?: undefined }
  | { add: CheckAddition; row?: undefined }
);

const capital = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);
const signed = (n: number) => (n > 0 ? `+${fmt(n)}` : n < 0 ? fmt(n) : "");

/** What a send records when staff go ahead despite the warning. */
export const sendAnyway = (check: DraftCheck) => ({ key: check.key, note: overrideNote(check) });

/** Its check was recorded as it was made (server/src/po-plan-check.ts recordDraftStart()), not by someone going through it. */
const checkedWhenDrafted = (po: Po) => !!po.plan_checked_at && Math.abs(new Date(po.plan_checked_at).getTime() - new Date(po.created_at).getTime()) < 120_000;

/** "Last checked against the plan 2 days ago by Jeff", "Drafted from the plan 2 days ago by Jeff". */
export function useLastChecked(po: Po): string {
  const users = useRows("app_user");
  if (!po.plan_checked_at) return "Not checked against the plan yet.";
  const who = users.find((u) => u.id === po.plan_checked_by)?.name;
  return `${checkedWhenDrafted(po) ? "Drafted" : "Last checked against the plan"} ${ago(po.plan_checked_at)}${who ? ` by ${who}` : ""}.`;
}

export function PoPlanCheck({
  po,
  lines,
  check,
  stale,
  terms,
}: {
  po: Po;
  lines: PoLine[];
  check: DraftCheck;
  stale: boolean;
  terms: Map<string, VendorItem>;
}) {
  const lastChecked = useLastChecked(po);
  const vendor = useRows("vendor").find((v) => v.id === po.vendor_id);
  // What the person ticked or unticked, per item, wins over the defaults; a new check (a sale, a sync) keeps it, and
  // only items they haven't touched follow the defaults.
  const [toggled, setToggled] = useState<ReadonlyMap<string, boolean>>(new Map());
  const [busy, setBusy] = useState(false);
  const editable = can("po.update_from_plan");
  const actionable = [...check.rows.filter((r) => r.action), ...check.additions];
  const ticked = new Set(actionable.filter((x) => toggled.get(x.itemId) ?? x.tick).map((x) => x.itemId));
  const count = ticked.size;
  const toggle = (id: string, on: boolean) => setToggled(new Map(toggled).set(id, on));
  const allIds = actionable.map((x) => x.itemId);
  const allOn = allIds.length > 0 && allIds.every((id) => ticked.has(id));

  const update = async () => {
    if (stale) return;
    // New lines get their ids here, so they show at once; the server works the numbers out again from its own check.
    const newIds = new Map(check.additions.map((a) => [a.itemId, crypto.randomUUID()]));
    const lineIdFor = (itemId: string) => newIds.get(itemId) ?? crypto.randomUUID();
    const items = updateItems(check, ticked, lineIdFor);
    if (!items.length) return;
    const args = planChanges(check, po.id, ticked, lineIdFor);
    // Shown at once: the new quantities (prices follow the vendor's breaks, as the server does), lines off, lines added.
    const now = new Date().toISOString();
    const changes: Change[] = [];
    for (const u of args.updates) {
      const l = lines.find((x) => x.id === u.line_id);
      if (!l) continue;
      const price = repriceFor(terms.get(l.item_id), l, u.qty);
      changes.push({
        t: "po_line",
        id: l.id,
        row: { ...l, qty: u.qty, auto_qty: null, plan_basis: u.plan_basis, ...(price !== undefined ? { unit_price: price } : {}), updated_at: now },
      });
    }
    for (const id of args.removes) changes.push({ t: "po_line", id, row: null });
    // A make PO's builds get their make order (BOM snapshot) on the server, so they show when it answers (~50 ms).
    if (po.kind !== "make") {
      let position = lines.reduce((n, l) => Math.max(n, l.position), 0);
      for (const a of args.adds) {
        const item = store.row("item", a.item_id) as { sku: string; name: string; photo_url: string } | undefined;
        if (!item) continue;
        changes.push({
          t: "po_line",
          id: a.id,
          row: {
            ...DEFAULTS.po_line,
            ...lineDefaults(item, terms.get(a.item_id), a.qty),
            id: a.id,
            po_id: po.id,
            item_id: a.item_id,
            qty: a.qty,
            position: ++position,
            plan_basis: a.plan_basis,
            created_at: now,
            updated_at: now,
          },
        });
      }
    }
    changes.push({ t: "po", id: po.id, row: { ...po, plan_checked_at: now, plan_checked_by: me()?.id ?? null, plan_check_key: args.key } });
    setBusy(true);
    const { ok, result } = await mutateWithResult("po.update_from_plan", { id: po.id, items }, changes);
    setBusy(false);
    if (!ok) return;
    setToggled(new Map());
    const r = result as { updated: number; removed: number; added: number; warnings: string[] } | undefined;
    const done = [r?.updated ? `${r.updated} changed` : "", r?.removed ? `${r.removed} taken off` : "", r?.added ? `${r.added} added` : ""].filter(Boolean);
    toast.ok(`${po.number} updated from the plan: ${done.join(", ") || "nothing to change"}`);
    if (r?.warnings?.length) toast.info(r.warnings.join(". "));
  };

  const keep = () =>
    mutateWithResult("po.plan_checked", { id: po.id, key: check.key }, [
      { t: "po", id: po.id, row: { ...po, plan_checked_at: new Date().toISOString(), plan_checked_by: me()?.id ?? null, plan_check_key: check.key } },
    ]);

  const differ = check.rows.filter((r) => isDifference(r.status));
  const vendorName = vendor?.short_name || vendor?.name || "this vendor";
  // Differences someone already went through (as it was drafted, or "Keep as they are") aren't a warning: only what
  // changed since is (Mitch, 2026-11-03).
  const seen = !!check.warning && !needsWarning(check, po.plan_check_key);
  // The check as the usual table: the draft's items, then (in their band) what's due and isn't on it. A tick is a
  // checkbox in its cell, only on the rows Update selected can change (row selection would put one on every row).
  const due: TableSection = { key: "due", label: `Due from ${vendorName} to this ship-to, not on this draft`, order: 1 };
  const positionOf = new Map(lines.map((l) => [l.id, l.position]));
  const planRows: PlanRow[] = [
    ...check.rows.map((r, i) => ({
      id: r.itemId,
      order: i,
      position: positionOf.get(r.lineIds[0]) ?? null,
      sku: r.sku,
      name: r.name,
      reason: r.reasons.join(" "),
      actionable: !!r.action,
      row: r,
    })),
    ...check.additions.map((a, i) => ({
      id: a.itemId,
      order: check.rows.length + i,
      position: null,
      sku: a.sku,
      name: a.name,
      reason: a.reasons.join(" "),
      actionable: true,
      add: a,
    })),
  ];
  const statusText = (x: PlanRow) => (x.add ? "Due, not on it" : x.row.status === "not_needed" ? "Not needed now" : LINE_STATUS_LABELS[x.row.status]);
  const changeOf = (x: PlanRow) => (x.add ? x.add.qty : x.row.status === "not_needed" ? -x.row.drafted : x.row.status === "same" ? 0 : x.row.diff);
  const setByText = (x: PlanRow) => (x.add ? (x.add.removedBefore ? "Taken off before" : "") : x.row.sourceText);
  const columns: Column<PlanRow>[] = [
    ...(editable
      ? [
          {
            key: "tick",
            header: "",
            width: "36px",
            value: (x: PlanRow) => (ticked.has(x.id) ? 0 : x.actionable ? 1 : 2),
            cell: (x: PlanRow) =>
              x.actionable && (
                <input
                  type="checkbox"
                  aria-label={`${x.add ? "Add" : "Update"} ${x.sku}`}
                  className="accent-[var(--accent)]"
                  checked={ticked.has(x.id)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => toggle(x.id, e.target.checked)}
                />
              ),
          },
        ]
      : []),
    {
      key: "position",
      header: "#",
      width: "40px",
      align: "right",
      value: (x) => x.order,
      text: (x) => x.position ?? "",
      cell: (x) => <span className="text-faint">{x.position ?? ""}</span>,
    },
    {
      key: "sku",
      header: "SKU",
      width: "minmax(110px,1fr)",
      cell: (x) => (
        <span className="truncate font-mono text-[12px]" title={x.name}>
          {x.sku}
        </span>
      ),
    },
    {
      key: "name",
      header: "Name",
      width: "minmax(150px,1.5fr)",
      cell: (x) => (
        <>
          <span className="truncate text-muted" title={x.name}>
            {x.name}
          </span>
          {x.row && x.row.lineIds.length > 1 && <Badge>{x.row.lineIds.length} lines</Badge>}
        </>
      ),
    },
    {
      key: "drafted",
      header: "Drafted",
      width: "80px",
      align: "right",
      value: (x) => x.row?.drafted ?? "",
      cell: (x) => (x.row ? <span className="font-mono">{fmt(x.row.drafted)}</span> : <span className="font-mono text-faint">—</span>),
    },
    {
      key: "plan",
      header: "Plan now",
      width: "84px",
      align: "right",
      value: (x) => (x.add ? x.add.qty : (x.row.plan ?? "")),
      cell: (x) =>
        x.add ? (
          <span className="font-mono font-semibold">{fmt(x.add.qty)}</span>
        ) : (
          <span className={cx("font-mono", x.row.status !== "same" && x.row.plan !== null && "font-semibold")}>
            {x.row.plan === null ? "—" : fmt(x.row.plan)}
          </span>
        ),
    },
    {
      key: "change",
      header: "Change",
      width: "76px",
      align: "right",
      value: changeOf,
      text: (x) => (x.add ? signed(x.add.qty) : x.row.status === "not_needed" ? fmt(-x.row.drafted) : x.row.status === "same" ? "" : signed(x.row.diff)),
      cell: (x) =>
        x.add ? (
          <span className="font-mono text-warn">{signed(x.add.qty)}</span>
        ) : (
          <span className={cx("font-mono", x.row.diff > 0 ? "text-warn" : "text-accent-text")}>
            {x.row.status === "not_needed" ? fmt(-x.row.drafted) : x.row.status === "same" ? "" : signed(x.row.diff)}
          </span>
        ),
    },
    {
      key: "status",
      header: "Status",
      width: "124px",
      value: (x) => STATUS_RANK[x.add ? "due" : x.row.status],
      text: statusText,
      cell: (x) => <Badge tone={x.add ? "warn" : TONE[x.row.status]}>{statusText(x)}</Badge>,
    },
    {
      key: "orderBy",
      header: "Order by",
      width: "110px",
      value: (x) => (x.add ? x.add.orderBy : x.row.orderBy) ?? "",
      text: (x) => (x.add ? x.add.orderByText : x.row.orderByText),
      cell: (x) => <span className="truncate text-muted">{x.add ? x.add.orderByText : x.row.orderByText || "—"}</span>,
    },
    {
      key: "setBy",
      header: "Set by",
      width: "140px",
      value: setByText,
      cell: (x) => (
        <span
          className={cx("truncate text-[12px]", x.row?.byPerson ? "text-text" : "text-muted")}
          title={x.row?.byPerson ? "Not ticked by default" : undefined}
        >
          {setByText(x)}
        </span>
      ),
    },
    {
      key: "why",
      header: "Why",
      width: "minmax(220px,2.2fr)",
      value: (x) => x.reason,
      cell: (x) => (
        <span className="truncate text-[12px] text-muted" title={x.reason}>
          {x.reason}
        </span>
      ),
    },
    {
      key: "explain",
      header: "",
      width: "40px",
      value: () => "",
      cell: (x) => <Why lines={whyOf.bind(null, check, x.add ? x.add.result : x.row.result)} sku={x.sku} />,
    },
  ];
  const asThen = [
    check.differences ? `${fmt(check.differences)} ${check.differences === 1 ? "line differs" : "lines differ"} from the plan` : "",
    check.missing
      ? `${fmt(check.missing)} ${check.missing === 1 ? "item" : "items"} due from ${vendorName} ${check.missing === 1 ? "isn't" : "aren't"} on it`
      : "",
  ]
    .filter(Boolean)
    .join(" and ");
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-start gap-3 border-b border-line px-4 py-2.5">
        {seen ? (
          <Info className="mt-0.5 size-4 shrink-0 text-muted" />
        ) : check.warning ? (
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warn" />
        ) : (
          <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-ok" />
        )}
        <div className="min-w-0 flex-1">
          <div className="font-medium">
            {seen
              ? `Nothing new since ${checkedWhenDrafted(po) ? "it was drafted" : "it was last checked"}: ${asThen}, as then.`
              : check.warning
                ? `${capital(check.warning)}.`
                : `Matches the plan: every line is what it suggests now, and nothing else from ${vendorName} is due.`}
          </div>
          <div className="mt-0.5 text-[12px] text-muted">
            {lastChecked} Compared with what the plan suggests today if this draft didn't exist (other POs still count)
            {po.cover_through ? `, buying to last to ${coverDay(po.cover_through, planToday())} as it was drafted` : ""}; differences inside the MOQ or order
            multiple, or under {CHECK_TOLERANCE_PCT}% of the need, count as the same.
          </div>
        </div>
        {editable && (
          <div className="flex shrink-0 items-center gap-2">
            <label className="inline-flex items-center gap-1.5 text-[12px] text-muted">
              <input
                type="checkbox"
                aria-label="Tick every change"
                className="accent-[var(--accent)]"
                checked={allOn}
                disabled={!allIds.length}
                onChange={(e) => setToggled(new Map(allIds.map((id) => [id, e.target.checked])))}
              />
              Tick every change
            </label>
            {check.warning && !seen && (
              <Button
                size="sm"
                variant="ghost"
                onClick={keep}
                title="Record that you went through the check and keep the draft as it is (sending won't warn about these differences again)"
              >
                Keep as they are
              </Button>
            )}
            <Button
              size="sm"
              variant="primary"
              disabled={!count || busy || stale}
              onClick={update}
              title={stale ? "Catching up with the latest changes…" : undefined}
            >
              Update selected{count ? ` (${count})` : ""}
            </Button>
          </div>
        )}
      </div>
      <div className={cx("min-h-0 flex-1 transition-opacity", stale && "opacity-70")}>
        <DataTable
          rows={planRows}
          columns={columns}
          defaultSort={{ key: "position" }}
          sectionOf={(r) => (r.add ? due : null)}
          exportName="Plan check"
          pageFilters={false}
          rowNoun={["item", "items"]}
          empty="No lines yet, and nothing is due from this vendor."
        />
      </div>
      {differ.some((r) => r.byPerson) && (
        <p className="shrink-0 border-t border-line px-4 py-2 text-[12px] text-faint">
          Lines someone typed or added by hand aren't ticked: they may be ordering ahead or rounding on purpose. Tick them to follow the plan.
        </p>
      )}
    </div>
  );
}

/** The plan's full why (explain()), worked out only when opened. */
function Why({ lines, sku }: { lines: () => string[]; sku: string }) {
  return (
    <Popover
      width={440}
      button={(toggle, open) => (
        <button
          type="button"
          title="The plan's why"
          onClick={toggle}
          className={cx("rounded p-1 hover:bg-hover hover:text-text", open ? "text-text" : "text-faint")}
        >
          <Info className="size-3.5" />
        </button>
      )}
    >
      {() => (
        <div className="text-[12px]">
          <div className="mb-1.5 font-mono font-medium">{sku}: the plan without this draft</div>
          <ol className="list-decimal space-y-1 pl-4 text-muted">
            {lines().map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ol>
        </div>
      )}
    </Popover>
  );
}

/**
 * The warning before "Mark as sent", never blocking: what differs from the plan (Review opens the check), Packaging ---
 * packaging parts whose revision lacks what the vendor prints from (each opens where it's fixed), Product labels ---
 * products whose label has something off (each opens the label part), or any of them together.
 */
export function SendConfirm({
  check,
  gaps,
  labels,
  sendLabel,
  onReview,
  onSend,
  onClose,
}: {
  /** Differences staff haven't gone through (needsWarning()), else null. */
  check: DraftCheck | null;
  gaps: readonly PrintGap[];
  labels: readonly LabelGap[];
  sendLabel: string;
  onReview: () => void;
  onSend: () => void;
  onClose: () => void;
}) {
  const openFix = useOpenFix();
  const openLabel = useOpenLabel();
  const shown = check ? check.rows.filter((r) => isDifference(r.status)).slice(0, 5) : [];
  const more = check ? check.differences - shown.length : 0;
  // What happens if they go ahead anyway, one sentence per warning shown.
  const after = [check && "that you went ahead is recorded on the PO.", gaps.length > 0 && later(gaps), labels.length > 0 && LABELS_LATER].filter(
    (t): t is string => !!t,
  );
  return (
    <DialogLayer
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="w-[560px] rounded-xl border border-line bg-surface p-4 shadow-pop">
        {check && (
          <>
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warn" />
              <div className="text-[15px] font-semibold">{capital(check.warning)}.</div>
            </div>
            <ul className="mt-2 ml-6 flex flex-col gap-1 text-[12px]">
              {shown.map((r) => (
                <li key={r.itemId}>
                  <span className="font-mono">{r.sku}</span>: {fmt(r.drafted)} drafted, the plan says {fmt(r.plan ?? 0)} (
                  {LINE_STATUS_LABELS[r.status].toLowerCase()}){r.reasons[0] && <span className="text-muted">. {r.reasons[0]}</span>}
                </li>
              ))}
              {more > 0 && (
                <li className="text-muted">
                  {more} more {more === 1 ? "line" : "lines"}
                </li>
              )}
              {check.additions.slice(0, 3).map((a) => (
                <li key={a.itemId}>
                  <span className="font-mono">{a.sku}</span>: due ({fmt(a.qty)}), not on this draft
                </li>
              ))}
              {check.additions.length > 3 && <li className="text-muted">{check.additions.length - 3} more due items</li>}
            </ul>
          </>
        )}
        {gaps.length > 0 && (
          <div className={check ? "mt-4" : undefined}>
            <div className="flex items-start gap-2">
              <Package className="mt-0.5 size-4 shrink-0 text-warn" />
              <div className={check ? "font-semibold" : "text-[15px] font-semibold"}>{notReady(gaps)}.</div>
            </div>
            <ul className="mt-2 ml-6 flex flex-col gap-1 text-[12px]">
              {gaps.slice(0, 6).map((g) => (
                <li key={g.item.id}>
                  <button
                    type="button"
                    className="font-mono text-accent-text hover:underline"
                    title={fixTitle(g)}
                    onClick={() => {
                      onClose();
                      openFix(g);
                    }}
                  >
                    {g.sku}
                  </button>
                  : {gapText(g)} <span className="text-muted">· {g.pack.title}</span>
                </li>
              ))}
              {gaps.length > 6 && <li className="text-muted">{gaps.length - 6} more</li>}
            </ul>
          </div>
        )}
        {labels.length > 0 && (
          <div className={check || gaps.length ? "mt-4" : undefined}>
            <div className="flex items-start gap-2">
              <Tag className="mt-0.5 size-4 shrink-0 text-warn" />
              <div className={check || gaps.length ? "font-semibold" : "text-[15px] font-semibold"}>{labelsOff(labels)}.</div>
            </div>
            <ul className="mt-2 ml-6 flex flex-col gap-1 text-[12px]">
              {labels.slice(0, 6).map((g) => (
                <li key={g.key}>
                  <button
                    type="button"
                    className="font-mono text-accent-text hover:underline"
                    title={LABEL_FIX_TITLE}
                    onClick={() => {
                      onClose();
                      openLabel(g);
                    }}
                  >
                    {g.sku}
                  </button>
                  : {g.text}
                </li>
              ))}
              {labels.length > 6 && <li className="text-muted">{labels.length - 6} more</li>}
            </ul>
          </div>
        )}
        <p className="mt-3 text-[12px] text-muted">You can send it as it is: {after.map((t, i) => (i ? capital(t) : t)).join(" ")}</p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} autoFocus={!check}>
            Cancel
          </Button>
          {check && (
            <Button onClick={onReview} autoFocus>
              Review
            </Button>
          )}
          <Button variant="primary" onClick={onSend}>
            {sendLabel}
          </Button>
        </div>
      </div>
    </DialogLayer>
  );
}

/** Packaging --- "2 packaging parts aren't ready for the vendor to print". */
const notReady = (gaps: readonly PrintGap[]) =>
  gaps.length === 1 ? "A packaging part isn't ready for the vendor to print" : `${gaps.length} packaging parts aren't ready for the vendor to print`;
/**
 * A line sent without what the vendor prints from gets it by a newer released revision and Send update (NewerSpecs):
 * now, when one already has it (fixedBy), else once it's released.
 */
function later(gaps: readonly PrintGap[]) {
  const fixed = gaps.filter((g) => g.fixedBy).length;
  if (!fixed) return "once what's missing is released, Send update gets it to the vendor.";
  const newer = fixed === 1 ? "the newer revision that has it" : "the newer revisions that have it";
  return fixed === gaps.length ? `Send update gets the vendor ${newer}.` : `Send update gets the vendor ${newer}, and the rest once it's released.`;
}

/** Product labels --- "2 product labels have something off". */
const labelsOff = (gaps: readonly LabelGap[]) => (gaps.length === 1 ? "A product label has something off" : `${gaps.length} product labels have something off`);

/** Product labels --- the label warning as a strip in the compose dialog, under the print files' (Send becomes "Send anyway"). */
export function LabelGapsBanner({ gaps, onOpen }: { gaps: readonly LabelGap[]; onOpen: (gap: LabelGap) => void }) {
  return (
    <div className="flex items-start gap-2 rounded-md bg-warn-soft px-3 py-2 text-[12px] text-warn">
      <Tag className="mt-px size-3.5 shrink-0" />
      <span className="min-w-0 flex-1">
        {labelsOff(gaps)}:{" "}
        {gaps.slice(0, 4).map((g, i) => (
          <Fragment key={g.key}>
            {i > 0 && "; "}
            <button type="button" className="font-mono font-medium underline hover:no-underline" title={LABEL_FIX_TITLE} onClick={() => onOpen(g)}>
              {g.sku}
            </button>
            : {g.text}
          </Fragment>
        ))}
        {gaps.length > 4 && `; ${gaps.length - 4} more`}. <span className="text-text/70">You can send it as it is: {LABELS_LATER}</span>
      </span>
    </div>
  );
}

/** Packaging --- the print-file warning as a strip in the compose dialog, under the plan's (Send becomes "Send anyway"). */
export function PrintGapsBanner({ gaps, onOpen }: { gaps: readonly PrintGap[]; onOpen: (gap: PrintGap) => void }) {
  return (
    <div className="flex items-start gap-2 rounded-md bg-warn-soft px-3 py-2 text-[12px] text-warn">
      <Package className="mt-px size-3.5 shrink-0" />
      <span className="min-w-0 flex-1">
        {notReady(gaps)}:{" "}
        {gaps.slice(0, 4).map((g, i) => (
          <Fragment key={g.item.id}>
            {i > 0 && "; "}
            <button type="button" className="font-mono font-medium underline hover:no-underline" title={fixTitle(g)} onClick={() => onOpen(g)}>
              {g.sku}
            </button>
            : {gapText(g)}
          </Fragment>
        ))}
        {gaps.length > 4 && `; ${gaps.length - 4} more`}. <span className="text-text/70">You can send it as it is: {later(gaps)}</span>
      </span>
    </div>
  );
}

/** The same warning as a strip in the compose dialog (its Send button says "Send anyway"). */
export function PlanCheckBanner({ check, onReview }: { check: DraftCheck; onReview: () => void }) {
  return (
    <div className="flex items-start gap-2 rounded-md bg-warn-soft px-3 py-2 text-[12px] text-warn">
      <AlertTriangle className="mt-px size-3.5 shrink-0" />
      <span className="min-w-0 flex-1">
        {capital(check.warning)}. <span className="text-text/70">You can send it as it is: that you went ahead is recorded on the PO.</span>
      </span>
      <button type="button" className="shrink-0 font-medium underline hover:no-underline" onClick={onReview}>
        Review
      </button>
    </div>
  );
}
